import { Box, Button, Typography, useTheme } from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";
import type { FeatureKey } from "../../services/feature-flags";

interface FeatureGuardProps {
  feature: FeatureKey;
  /**
   * Что показывать вместо children, если фича выключена.
   * По умолчанию — красивый full-page блокер «Функция временно недоступна».
   */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Оборачивает содержимое страницы. Если админ выключил фичу
 * в /settings — показываем блокер вместо содержимого.
 * Используем на защищённых страницах (например `/cards`, `/deposit`,
 * `/withdraw`, `/pay-qr`) чтобы прямой переход по URL / из нижнего
 * меню тоже был перехвачен.
 */
export function FeatureGuard({ feature, fallback, children }: FeatureGuardProps) {
  const { isFeatureEnabled, getFeatureMessage } = useFeatureFlags();
  const { t } = useTranslation("common");

  if (isFeatureEnabled(feature)) {
    return <>{children}</>;
  }

  const message = getFeatureMessage(feature) ?? t("feature_guard.default_message");
  return <>{fallback ?? <DefaultFeatureBlocker message={message} />}</>;
}

// ------------------------------------------------------------------
// Дефолтный блокер: центрированный контент, иконка «инструменты»
// и кнопка «На главную» (или history.back).
// ------------------------------------------------------------------
function DefaultFeatureBlocker({ message }: { message: string }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("common");

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 20px",
        textAlign: "center",
        gap: "16px",
      }}
    >
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(245, 158, 11, 0.12)"
              : "rgba(245, 158, 11, 0.14)",
          color: "#F59E0B",
        }}
      >
        <Icon icon="solar:settings-minimalistic-bold-duotone" width={54} height={54} />
      </Box>

      <Typography sx={{ fontSize: 20, fontWeight: 700, marginTop: "4px" }}>
        {t("feature_guard.title")}
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          color: "text.secondary",
          maxWidth: 320,
          lineHeight: 1.5,
        }}
      >
        {message}
      </Typography>

      <Button
        variant="contained"
        onClick={() => navigate("/")}
        sx={{ marginTop: "12px", borderRadius: "999px", paddingInline: "24px" }}
      >
        {t("feature_guard.home")}
      </Button>
    </Box>
  );
}
